'use client'

import { useState } from 'react'
import { useRouter } from 'next/navigation'
import { HugeiconsIcon } from '@hugeicons/react'
import {
  MagicWand01Icon,
  PencilEdit01Icon,
  PlusSignIcon,
} from '@hugeicons-pro/core-stroke-rounded'
import { Popover, PopoverContent, PopoverTrigger } from '@/components/ui/popover'
import { useChat } from '@/contexts/ChatContext'

type NewCommandPopoverProps = {
  trigger?: React.ReactNode
}

export function NewCommandPopover({ trigger }: NewCommandPopoverProps) {
  const router = useRouter()
  const { openChat } = useChat()
  const [open, setOpen] = useState(false)

  const createThroughChat = () => {
    setOpen(false)
    openChat(undefined, {
      seedMessage: 'Help me create a new command. What should it do?',
      autoSendSeed: true,
    })
  }

  const createManually = () => {
    setOpen(false)
    router.push('/commands/new')
  }

  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>
        {trigger ?? (
          <button
            type="button"
            className="bv3-btn-grn"
            style={{ padding: '8px 14px', fontSize: 13, display: 'flex', alignItems: 'center', gap: 6 }}
          >
            <HugeiconsIcon icon={PlusSignIcon} className="size-4" />
            New command
          </button>
        )}
      </PopoverTrigger>
      <PopoverContent align="end" className="w-80 bg-white p-2 text-atrium-black">
        <div className="grid gap-2">
          <button
            type="button"
            className="group flex items-center gap-3 rounded-xl border border-atrium-border bg-atrium-inner p-4 text-left transition-colors hover:bg-atrium-content"
            onClick={createThroughChat}
          >
            <span className="grid size-10 shrink-0 place-items-center rounded-lg bg-accent-600 text-white">
              <HugeiconsIcon icon={MagicWand01Icon} className="size-5" />
            </span>
            <span className="min-w-0">
              <span className="block text-sm font-semibold text-atrium-black">Create through chat</span>
              <span className="mt-0.5 block text-xs text-atrium-muted">Tell the copilot what you want to automate.</span>
            </span>
          </button>
          <button
            type="button"
            className="group flex items-center gap-3 rounded-xl border border-atrium-border bg-atrium-inner p-4 text-left transition-colors hover:bg-atrium-content"
            onClick={createManually}
          >
            <span className="grid size-10 shrink-0 place-items-center rounded-lg bg-atrium-content text-atrium-black">
              <HugeiconsIcon icon={PencilEdit01Icon} className="size-5" />
            </span>
            <span className="min-w-0">
              <span className="block text-sm font-semibold text-atrium-black">Create manually</span>
              <span className="mt-0.5 block text-xs text-atrium-muted">Configure every field yourself.</span>
            </span>
          </button>
        </div>
      </PopoverContent>
    </Popover>
  )
}
